/**
 * @fileoverview Reusable Confirmation Modal Component
 *
 * A simple, reusable modal for confirming actions.
 * Displays a title, message, and confirm/cancel buttons.
 *
 * @version 1.0.0
 */

import React from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import "./ConfirmationModal.css";

/**
 * ConfirmationModal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onConfirm - Confirm button handler
 * @param {string} props.title - Modal title
 * @param {string} props.message - Confirmation message
 * @param {string} props.confirmText - Confirm button text (default: "Confirm")
 * @param {string} props.cancelText - Cancel button text (default: "Cancel")
 * @param {boolean} props.isDestructive - Whether action is destructive/warning (default: false)
 * @param {boolean} props.isLoading - Whether confirm action is in progress (default: false)
 */
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "",
  item = "",
  punc = "",
  warning = "This action cannot be undone.",
  proceed = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  isDestructive = false,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  const handleConfirm = async () => {
    try {
      await onConfirm();
      // Note: Parent component should handle closing modal after successful action
    } catch (error) {
      // Let parent handle errors - don't close modal on error
      console.error("[ConfirmationModal] Confirm error:", error);
    }
  };

  return (
    <div className="confirmation-modal-overlay" onClick={onClose}>
      <div className="cm-content" onClick={(e) => e.stopPropagation()}>
        {/* Title */}
        <div className="cm-title">
          <FaExclamationTriangle />
          <h3>{title}</h3>
        </div>
        <div className="cm-text">
          {/* Message */}
          <p>
            {message} <span className="cm-text-item">{item}</span>
            {punc} {"  "} {warning} <u>{proceed}</u>
          </p>
        </div>
        {/* Actions */}
        <div className="cm-actions">
          <button
            className="confirmation-modal-btn cancel"
            onClick={onClose}
            disabled={isLoading}
            aria-label="Cancel"
          >
            {cancelText}
          </button>
          <button
            className={`confirmation-modal-btn confirm ${isDestructive ? "destructive" : ""}`}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? "Processing..." : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
